import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Gauge } from "lucide-react";
import { api, CAP_LABELS } from "@/lib/api";
import { SectionHead, Reveal } from "@/components/Reveal";

export default function TodayWorkout() {
  const [today, setToday] = useState(null);

  useEffect(() => {
    api.get("/public/today").then((r) => setToday(r.data)).catch(() => {});
  }, []);

  if (!today) return null;

  return (
    <section data-testid="today-workout-section" className="bg-ink py-24 sm:py-32 px-5 sm:px-10 lg:px-16">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <SectionHead overline="Today's Workout — live from the floor">
          On the floor <span className="text-stroke">today.</span>
        </SectionHead>
        <Reveal delay={0.12}>
          <div className="flex gap-6 text-xs uppercase tracking-[0.16em] text-zinc-400 font-bold">
            <span className="flex items-center gap-2"><Clock size={14} className="text-volt" /> {today.duration_minutes} min</span>
            <span className="flex items-center gap-2"><Gauge size={14} className="text-volt" /> RPE {today.target_rpe}</span>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <div className="border border-zinc-800 bg-zinc-950">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800 p-6 sm:p-8">
            <div>
              <p className="font-display uppercase text-3xl sm:text-4xl leading-none">{today.name}</p>
              <p className="mt-2 text-sm text-zinc-400 max-w-xl">{today.objective}</p>
            </div>
            <span className="font-display text-volt text-xl tracking-widest border border-volt/40 px-4 py-2">
              {CAP_LABELS[today.primary_capability]}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3">
            {today.blocks.map((b) => (
              <div key={b.key} data-testid={`today-block-${b.key}`} className="p-6 sm:p-8 border-b sm:border-r border-zinc-800/70">
                <div className="flex items-baseline justify-between">
                  <p className="font-display uppercase text-2xl">{b.name}</p>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-volt font-bold">{b.minutes} min</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1 mb-4">{b.focus}</p>
                <ul className="space-y-2">
                  {b.items.map((i) => (
                    <li key={i.name} className="flex justify-between gap-3 text-sm">
                      <span className="text-zinc-200">{i.name}</span>
                      <span className="text-zinc-500 whitespace-nowrap">{i.prescription}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-zinc-500 uppercase tracking-[0.16em]">
              Preview of today's group session — generated by the Capable 360 training engine
            </p>
            <Link
              to="/sessions"
              data-testid="today-view-sessions-link"
              className="group inline-flex items-center gap-2 text-volt text-xs uppercase tracking-[0.18em] font-bold hover:text-white transition-colors"
            >
              Join a group session <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
